import { useState, useEffect } from 'react';
import { formatDateUK } from '../utils/dateUtils';
import { handleApiError } from '../utils/errorHandler';
import {
  Shield,
  Smartphone,
  Download,
  Trash2,
  Eye,
  EyeOff,
  Key,
  Monitor,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { twoFactorAPI } from '../utils/api';
import { toast } from 'react-toastify';
import TwoFactorSetup from '../components/TwoFactorSetup';

const SecuritySettings = () => {
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    setupCompleted: false,
    backupCodesCount: 0,
    trustedDevicesCount: 0,
  });
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disableForm, setDisableForm] = useState({
    password: '',
    token: '',
    backupCode: '',
  });
  const [backupCodes, setBackupCodes] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      const [statusResponse, devicesResponse] = await Promise.all([
        twoFactorAPI.getStatus(),
        twoFactorAPI.getTrustedDevices(),
      ]);

      setTwoFactorStatus(statusResponse.data);
      setTrustedDevices(devicesResponse.data.devices || []);
    } catch (error) {
      handleApiError(error, 'Failed to load security settings');
    }
  };

  const handleDisable2FA = async () => {
    if (!disableForm.password) {
      toast.error('Password is required');
      return;
    }

    if (!disableForm.token && !disableForm.backupCode) {
      toast.error('2FA token or backup code is required');
      return;
    }

    try {
      setLoading(true);
      await twoFactorAPI.disable(disableForm.password, disableForm.token, disableForm.backupCode);

      toast.success('2FA disabled successfully');
      setShowDisable(false);
      setDisableForm({ password: '', token: '', backupCode: '' });
      loadSecurityData();
    } catch (error) {
      handleApiError(error, 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    if (!disableForm.password) {
      toast.error('Password is required to generate new backup codes');
      return;
    }

    try {
      setLoading(true);
      const response = await twoFactorAPI.generateBackupCodes(disableForm.password);
      setBackupCodes(response.data.backupCodes);
      setShowBackupCodes(true);
      setDisableForm({ ...disableForm, password: '' });
      loadSecurityData();
    } catch (error) {
      handleApiError(error, 'Failed to generate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTrustedDevice = async deviceId => {
    if (!window.confirm('Are you sure you want to remove this trusted device?')) {
      return;
    }

    try {
      await twoFactorAPI.removeTrustedDevice(deviceId);
      toast.success('Trusted device removed');
      loadSecurityData();
    } catch (error) {
      handleApiError(error, 'Failed to remove trusted device');
    }
  };

  const downloadBackupCodes = () => {
    const content = `BookingApp 2FA Backup Codes\nGenerated: ${formatDateUK(new Date())}\n\n${backupCodes.join('\n')}\n\nKeep these codes safe! Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking-app-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>
        <p className="text-gray-600 mt-1">
          Manage your account security and authentication methods
        </p>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h3>
            </div>
            <div className="flex items-center">
              {twoFactorStatus.enabled ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Enabled
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Disabled
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-600 mb-4">
            Add an extra layer of security to your account by enabling two-factor authentication.
          </p>

          {!twoFactorStatus.enabled ? (
            <button
              onClick={() => setShowSetup(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              <Smartphone className="h-4 w-4 inline mr-2" />
              Enable 2FA
            </button>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Backup Codes</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {twoFactorStatus.backupCodesCount} unused codes remaining
                  </p>
                  <button
                    onClick={() => setShowBackupCodes(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Generate New Codes
                  </button>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Trusted Devices</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {twoFactorStatus.trustedDevicesCount} trusted devices
                  </p>
                  {twoFactorStatus.trustedDevicesCount > 0 && (
                    <button
                      onClick={() => setShowDisable(false)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Manage Devices
                    </button>
                  )}
                </div>
              </div>

              <button
                onClick={() => setShowDisable(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Disable 2FA
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Trusted Devices */}
      {trustedDevices.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Monitor className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Trusted Devices</h3>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="space-y-3">
              {trustedDevices.map(device => (
                <div
                  key={device.deviceId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{device.deviceName}</p>
                    <p className="text-sm text-gray-600">
                      Last used: {formatDateUK(device.lastUsed)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Expires: {formatDateUK(device.expiresAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveTrustedDevice(device.deviceId)}
                    className="text-red-600 hover:text-red-800"
                    title="Remove device"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Session Management */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Key className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Session Management</h3>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-600 mb-4">
            Your session will automatically expire after 24 hours for security. You can logout from
            all devices if needed.
          </p>

          <div className="space-y-3">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Automatic Session Expiry</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    For security, you'll be automatically logged out after 24 hours of inactivity.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                if (window.confirm('This will log you out from all devices. Continue?')) {
                  try {
                    await twoFactorAPI.logoutAll();
                    toast.success('Logged out from all devices');
                    window.location.href = '/login';
                  } catch (error) {
                    handleApiError(error, 'Failed to logout from all devices', false);
                  }
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Logout All Devices
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showSetup && (
        <TwoFactorSetup
          onClose={() => setShowSetup(false)}
          onComplete={() => {
            setShowSetup(false);
            loadSecurityData();
          }}
        />
      )}

      {/* Disable 2FA Modal */}
      {showDisable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Disable Two-Factor Authentication
              </h3>
              <button
                onClick={() => setShowDisable(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 mr-2" />
                  <p className="text-sm text-red-700">
                    Disabling 2FA will make your account less secure. Please confirm your identity.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={disableForm.password}
                    onChange={e =>
                      setDisableForm({
                        ...disableForm,
                        password: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2FA Token or Backup Code
                </label>
                <input
                  type="text"
                  value={disableForm.token || disableForm.backupCode}
                  onChange={e => {
                    const value = e.target.value;
                    if (value.length === 6 && /^\d+$/.test(value)) {
                      setDisableForm({
                        ...disableForm,
                        token: value,
                        backupCode: '',
                      });
                    } else {
                      setDisableForm({
                        ...disableForm,
                        backupCode: value.toUpperCase(),
                        token: '',
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="6-digit code or backup code"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDisable2FA}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-lg"
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
                <button
                  onClick={() => setShowDisable(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Backup Codes Modal */}
      {showBackupCodes && !backupCodes.length && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Generate New Backup Codes</h3>
              <button
                onClick={() => setShowBackupCodes(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                  <p className="text-sm text-yellow-700">
                    Generating new codes will invalidate your existing backup codes.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={disableForm.password}
                  onChange={e => setDisableForm({ ...disableForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter your password"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleGenerateBackupCodes}
                  disabled={loading || !disableForm.password}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-lg"
                >
                  {loading ? 'Generating...' : 'Generate Codes'}
                </button>
                <button
                  onClick={() => setShowBackupCodes(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Backup Codes Modal */}
      {showBackupCodes && backupCodes.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Your New Backup Codes</h3>
              <button
                onClick={() => {
                  setShowBackupCodes(false);
                  setBackupCodes([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                  <p className="text-sm text-yellow-700">
                    Save these codes in a safe place. Each code can only be used once.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="bg-white p-2 rounded border text-center">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={downloadBackupCodes}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join('\n'));
                    toast.success('Backup codes copied to clipboard');
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Copy All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
